import { ContentLink, LoadStatus } from '@servicetitan/cp-ui';
import { Banner, BodyText, Button, Divider, Form, Link, Stack } from '@servicetitan/design-system';
import { provide, useDependencies } from '@servicetitan/react-ioc';
import { SettingsApi } from 'api';
import { observer } from 'mobx-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthLoginStore } from '../../stores/login.store';
import { LoginResult } from '../../stores/portal-auth.store';
import { SettingsStore } from '../../stores/settings.store';
import { enableAutoValidationOnValidationError } from '../../utils/field-auto-validate-helper';
import { handleEmailSpaceKeyDown } from '../../utils/field-email-helper';
import { termsLink } from '../../utils/terms';
import {
    useAuthCodeParams,
    useAuthHistory,
    useAuthLoginQueryParams,
} from '../../utils/use-auth-history';
import { GoogleLogin } from '../google';
import { AuthLayout } from '../layout/auth.layout';
import { AuthHeadline } from '../layout/components';
// import { MicrosoftLogin } from '../microsoft';
import { InfoTipLabel } from '../ui/info';
import { PasswordInput } from '../ui/password.input';

export const AuthLoginProvider = provide({
    singletons: [AuthLoginStore, SettingsStore, SettingsApi],
})(({ children }) => children);

export const AuthLoginComponent = observer(() => {
    const [
        { canSubmit, form, init, submit, submitResult, submitStatus },
        { failedToLoadSettings, loadingSettings, permissionSettings },
    ] = useDependencies(AuthLoginStore, SettingsStore);
    const { email, keepSigned, password } = form.$;

    const history = useAuthHistory();
    const rootNavigate = useNavigate();
    const params = useAuthLoginQueryParams();
    const { code } = useAuthCodeParams();
    const isSubmitting = submitStatus === LoadStatus.Loading;
    const displayRegisterLink = loadingSettings
        ? false
        : failedToLoadSettings || !permissionSettings?.requireInviteToSignUp;

    useEffect(() => {
        init(code);
    }, [init, code]);

    useEffect(() => {
        if (submitStatus === LoadStatus.Ok) {
            if (code) {
                history.replace('invite', { code }, { auto: true });
            } else {
                rootNavigate(params.redirect ? decodeURI(params.redirect) : '/');
            }
        }
    }, [submitStatus, rootNavigate, history, params.redirect, code]);

    return (
        <AuthLayout>
            {submitStatus === LoadStatus.Error ? (
                <Banner
                    status="warning"
                    icon
                    title={
                        submitResult === LoginResult.Restricted
                            ? 'You do not have an account with us. Please, contact support for more information.'
                            : 'Invalid email or password'
                    }
                    className="m-b-2"
                />
            ) : params.info === 'ok' ? (
                <Banner status="success" icon title="Successful registration" className="m-b-2">
                    Now you can sign in with your credentials
                </Banner>
            ) : params.info === 'exists' ? (
                <Banner
                    status="info"
                    icon
                    title="It looks like you’re registered. Go ahead and sign in."
                    className="m-b-2"
                />
            ) : null}

            <AuthHeadline title="Sign In" />

            <Stack direction="column" className="m-b-3" spacing={2}>
                <GoogleLogin />
                {/* <MicrosoftLogin /> */}
                <Divider className="m-t-2">
                    <BodyText subdued size="small" className="m-x-1">
                        or log in to continue
                    </BodyText>
                </Divider>
            </Stack>

            <Form>
                <Form.Input
                    label="Email"
                    className="m-t-2"
                    value={email.value}
                    onChange={email.onChangeHandler}
                    onKeyDown={handleEmailSpaceKeyDown(email)}
                    onBlur={enableAutoValidationOnValidationError(email)}
                    error={email.error}
                    disabled={isSubmitting}
                    autoComplete="email"
                    type="email"
                />

                <PasswordInput
                    label="Password"
                    value={password.value}
                    onChange={password.onChangeHandler}
                    onBlur={enableAutoValidationOnValidationError(password)}
                    error={password.error}
                    disabled={isSubmitting}
                />
                <Form.Checkbox
                    checked={keepSigned.value}
                    onChange={keepSigned.onChangeHandler}
                    disabled={isSubmitting}
                    label={
                        <InfoTipLabel label="Keep me signed in">
                            Checking this box will reduce the number of times you’re asked to sign
                            in. To keep your account secure, use this option only on your personal
                            devices.
                        </InfoTipLabel>
                    }
                />

                <Button
                    width="full"
                    primary
                    loading={isSubmitting}
                    onClick={submit}
                    disabled={!canSubmit}
                >
                    Sign in with Email
                </Button>

                <BodyText className="ta-center m-t-2 c-neutral-200" size="small">
                    By signing in you agree to the{' '}
                    <Link href={termsLink} target="_blank" className="c-neutral-200">
                        Terms of Service
                    </Link>
                    .
                </BodyText>

                <div className="ta-center m-t-3">
                    <ContentLink href={history.href('recovery')} primary>
                        Can't sign in?
                    </ContentLink>
                </div>

                {displayRegisterLink && (
                    <BodyText className="ta-center m-t-2 ">
                        Don't have an account?{' '}
                        <ContentLink href={history.href('register')} primary>
                            Register
                        </ContentLink>
                    </BodyText>
                )}
            </Form>
        </AuthLayout>
    );
});

export const AuthLogin = () => (
    <AuthLoginProvider>
        <AuthLoginComponent />
    </AuthLoginProvider>
);
